import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  XMarkIcon,
  FunnelIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import AdminHeader from '../../../shared/components/AdminHeader';
import { useAuth } from '../../../core/providers/AuthProvider';
import { ROLES } from '../../../shared/constant/roles';
import { getOwnSectionGroups } from '../../Subject/services/subjectService';
import { getOwnAdvisory } from '../../Teacher/services/teacherService';
import Pagination from '../../../shared/components/Pagination';
import Spinner from '../../../shared/components/Spinner';
import { GRADE_LEVELS, getGradeLevelLabel } from '../../../shared/constant/gradeLevels';
import { confirmAction, showAlert, showSuccessToast } from '../../../shared/utils/dialogs';
import {
  createStudent,
  deleteStudent,
  getDistinctSections,
  getPendingStudentCount,
  getExistingLrns,
  getStudents,
  importStudents,
  setStudentApproval,
  updateStudent,
} from '../services/studentService';
import StudentFormModal from './StudentFormModal';
import ImportReviewModal from './ImportReviewModal';
import { parseSf1File } from '../utils/studentImport';

const PAGE_SIZE = 10;

function StudentsPage() {
  const { role } = useAuth();
  const location = useLocation();
  const isTeacherRole = role === ROLES.TEACHER;
  // Anyone here can add students, but only a super admin's start approved; the rest wait for one.
  const isSuperAdmin = role === ROLES.SUPER_ADMIN;
  // For teachers: the grade level + section pairs they teach (null until loaded). Everyone else sees all students.
  const [teacherGroups, setTeacherGroups] = useState(null);
  const [students, setStudents] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [section, setSection] = useState('');
  // '' = everyone, 'pending' = waiting for approval, 'has_login' = can sign in,
  // 'no_login' = no password set yet, 'must_change' = still on the login staff gave them.
  // Arriving from the notification bell lists the students it was about.
  const [status, setStatus] = useState(location.state?.fromNotification || '');
  const seenLocationKey = useRef(location.key);
  const [pendingCount, setPendingCount] = useState(0);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStudent, setEditingStudent] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // The SF1 file being reviewed: { fileName, details, students, existingLrns }, or null.
  const [importReview, setImportReview] = useState(null);
  const [readingFile, setReadingFile] = useState(false);
  const fileInputRef = useRef(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    if (!isTeacherRole) return;
    // Sections they teach, plus the advisory section (which may be one they teach no subject in).
    Promise.all([getOwnSectionGroups(), getOwnAdvisory()])
      .then(([taught, advisory]) => {
        const groups = new Map(taught.map((g) => [`${g.grade_level}|${g.section}`, g]));
        if (advisory) groups.set(`${advisory.grade_level}|${advisory.section}`, advisory);
        setTeacherGroups([...groups.values()]);
      })
      .catch((err) => {
        showAlert({ text: err.message || 'Failed to load your sections' });
        setTeacherGroups([]);
      });
  }, [isTeacherRole]);

  const loadStudents = useCallback(async () => {
    if (isTeacherRole && teacherGroups === null) return;

    setLoading(true);
    try {
      const { data, count } = await getStudents({
        search,
        gradeLevel,
        section,
        status,
        groups: isTeacherRole ? teacherGroups : undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setStudents(data);
      setTotalCount(count);
    } catch (err) {
      showAlert({ text: err.message || 'Failed to load students' });
    } finally {
      setLoading(false);
    }
  }, [search, gradeLevel, section, status, page, isTeacherRole, teacherGroups]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const loadPendingCount = useCallback(() => {
    if (!isSuperAdmin) return;
    getPendingStudentCount()
      .then(setPendingCount)
      .catch(() => {
        // The banner just stays as it was; the Pending filter still works.
      });
  }, [isSuperAdmin]);

  useEffect(() => {
    loadPendingCount();
  }, [loadPendingCount]);

  // Clicking the bell while already on this page navigates to the same route, so nothing remounts:
  // switch to the list it was about here (refreshing it even if it was already showing).
  useEffect(() => {
    if (location.key === seenLocationKey.current) return;
    seenLocationKey.current = location.key;
    const notificationStatus = location.state?.fromNotification;
    if (!notificationStatus) return;
    setSearch('');
    setGradeLevel('');
    setSection('');
    setPage(1);
    if (status === notificationStatus) loadStudents();
    else setStatus(notificationStatus);
    loadPendingCount();
  }, [location, status, loadStudents, loadPendingCount]);

  useEffect(() => {
    if (isTeacherRole) {
      if (teacherGroups) setSections([...new Set(teacherGroups.map((g) => g.section))].sort());
      return;
    }
    getDistinctSections()
      .then(setSections)
      .catch(() => setSections([]));
  }, [isTeacherRole, teacherGroups]);

  const gradeLevelOptions = isTeacherRole
    ? GRADE_LEVELS.filter((g) => teacherGroups?.some((group) => group.grade_level === g.value))
    : GRADE_LEVELS;

  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const hasActiveFilters = Boolean(search || gradeLevel || section || status);

  const handleClearFilters = () => {
    setSearch('');
    setGradeLevel('');
    setSection('');
    setStatus('');
    setPage(1);
  };

  const openCreateForm = () => {
    setEditingStudent(null);
    setIsFormOpen(true);
  };

  const openEditForm = (student) => {
    setEditingStudent(student);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (values) => {
    if (editingStudent) {
      await updateStudent(editingStudent.id, values);
      showSuccessToast('Student updated');
    } else {
      await createStudent(values);
      showSuccessToast(
        isSuperAdmin ? 'Student added' : 'Student added. An administrator must approve them before they can sign in.'
      );
    }
    setIsFormOpen(false);
    setEditingStudent(null);
    loadStudents();
    loadPendingCount();
  };

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // so choosing the same file again still fires
    if (!file) return;

    setReadingFile(true);
    try {
      const { details, students: parsed } = await parseSf1File(file);
      const existingLrns = await getExistingLrns(parsed.filter((s) => !s.error).map((s) => s.lrn));
      setImportReview({ fileName: file.name, details, students: parsed, existingLrns });
    } catch (err) {
      showAlert({ title: "Couldn't read that file", text: err.message || 'Make sure it is a DepEd SF1 Excel file.' });
    } finally {
      setReadingFile(false);
    }
  };

  const handleImport = async (rows) => {
    const { imported, failed } = await importStudents(rows);
    setImportReview(null);
    loadStudents();
    loadPendingCount();

    const summary = `${imported} student${imported === 1 ? '' : 's'} imported.${
      imported > 0 && !isSuperAdmin ? ' An administrator must approve them before they can sign in.' : ''
    }`;
    if (failed.length === 0) {
      showSuccessToast(summary);
      return;
    }
    const lines = failed
      .slice(0, 5)
      .map(({ student, message }) => `${student.lastname}, ${student.firstname} (${student.lrn}): ${message}`);
    if (failed.length > 5) lines.push(`…and ${failed.length - 5} more.`);
    showAlert({
      title: imported > 0 ? 'Some students were not imported' : 'No students were imported',
      text: `${summary} ${lines.join(' ')}`,
      icon: imported > 0 ? 'warning' : 'error',
    });
  };

  // Super admins only. Taking approval back signs the student out and takes them off grading, so it
  // asks first; approving doesn't.
  const handleToggleApproved = async (student) => {
    const name = `${student.firstname} ${student.lastname}`;
    if (student.approved) {
      const confirmed = await confirmAction({
        title: 'Set student to pending?',
        text: `${name} will be signed out, won't be able to sign in, and will be left out of subject grades and attendance until approved again.`,
        confirmText: 'Set to Pending',
        danger: true,
      });
      if (!confirmed) return;
    }

    try {
      await setStudentApproval(student.id, !student.approved, name);
      showSuccessToast(student.approved ? 'Student set to pending' : 'Student approved');
      loadStudents();
      loadPendingCount();
    } catch (err) {
      showAlert({ text: err.message || 'Failed to update approval status' });
    }
  };

  const showPending = () => {
    setSearch('');
    setGradeLevel('');
    setSection('');
    setStatus('pending');
    setPage(1);
  };

  const handleDelete = async (student) => {
    const name = `${student.firstname} ${student.lastname}`;
    const confirmed = await confirmAction({
      title: 'Remove student?',
      text: `${name} will be removed.`,
      confirmText: 'Remove',
      danger: true,
    });
    if (!confirmed) return;

    try {
      await deleteStudent(student.id, name);
      showSuccessToast('Student removed');
      loadStudents();
      loadPendingCount();
    } catch (err) {
      showAlert({ text: err.message || 'Failed to remove student' });
    }
  };

  return (
    <div className="h-dvh w-full flex flex-col bg-appbg overflow-hidden">
      <AdminHeader />

      <main className="flex-1 min-h-0 w-full p-4 lg:w-4/5 lg:mx-auto flex flex-col space-y-4">
        <div className="shrink-0 flex items-center gap-2">
          <Link to="/dashboard" aria-label="Back to dashboard" className="text-[#0b4a71] hover:text-[#3f6d8e]">
            <ArrowLeftIcon className="h-6 w-6" />
          </Link>
          <h1 className="text-2xl font-semibold text-[#0b4a71]">Students</h1>
        </div>

        {isSuperAdmin && pendingCount > 0 && status !== 'pending' && (
          <div className="shrink-0 flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-700">
            <span>
              {pendingCount} student{pendingCount === 1 ? '' : 's'} added by teachers {pendingCount === 1 ? 'is' : 'are'} waiting
              for your approval.
            </span>
            <button type="button" onClick={showPending} className="shrink-0 font-semibold underline">
              Review
            </button>
          </div>
        )}

        <div className="shrink-0 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <div className="flex gap-2 w-full sm:contents">
              <div className="relative w-full sm:flex-1 sm:min-w-0">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by LRN or name..."
                  value={search}
                  onChange={(e) => handleFilterChange(setSearch)(e.target.value)}
                  className="w-full rounded-md border border-[#4ea6d0] pl-9 pr-3 py-2 lg:text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
                />
              </div>

              <button
                type="button"
                onClick={() => setFiltersOpen((open) => !open)}
                aria-label="Toggle filters"
                className={`sm:hidden shrink-0 rounded-md border border-[#4ea6d0] p-2 ${
                  filtersOpen ? 'bg-[#4ea6d0] text-white' : 'text-[#0b4a71] hover:bg-gray-50'
                }`}
              >
                <FunnelIcon className="h-5 w-5" />
              </button>
            </div>

            <div className={`${filtersOpen ? 'flex' : 'hidden'} sm:flex flex-col sm:flex-row gap-3 w-full sm:flex-[2] sm:min-w-0`}>
              <select
                value={gradeLevel}
                onChange={(e) => handleFilterChange(setGradeLevel)(e.target.value)}
                className="sm:flex-1 sm:min-w-0 rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
              >
                <option value="">All Grade Levels</option>
                {gradeLevelOptions.map((g) => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>

              <select
                value={section}
                onChange={(e) => handleFilterChange(setSection)(e.target.value)}
                className="sm:flex-1 sm:min-w-0 rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
              >
                <option value="">All Sections</option>
                {sections.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              <select
                value={status}
                onChange={(e) => handleFilterChange(setStatus)(e.target.value)}
                aria-label="Filter by status"
                className="sm:flex-1 sm:min-w-0 rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
              >
                <option value="">All Status</option>
                <option value="pending">Pending approval</option>
                <option value="has_login">Can sign in</option>
                <option value="no_login">No login</option>
                <option value="must_change">Hasn't changed login</option>
              </select>

              <button
                type="button"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                className="flex items-center justify-center gap-1.5 rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 shrink-0"
              >
                <XMarkIcon className="h-4 w-4" />
                Clear Filter
              </button>
            </div>

            {/* Outside the filter group so it stays visible on phones, where the filters fold away. */}
            {/* A teacher can only add students to their own sections, so they need at least one. */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.xlsm"
              onChange={handleFileChosen}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={readingFile || (isTeacherRole && !teacherGroups?.length)}
              title={isTeacherRole && teacherGroups?.length === 0 ? 'No sections are assigned to you yet.' : 'Import students from a DepEd SF1 Excel file'}
              className="flex items-center justify-center gap-1.5 rounded-md border border-[#0b4a71] px-3 py-2 lg:text-sm font-medium text-[#0b4a71] hover:bg-gray-50 disabled:opacity-50 shrink-0"
            >
              <ArrowUpTrayIcon className="h-4 w-4" />
              {readingFile ? 'Reading…' : 'Import SF1'}
            </button>
            <button
              type="button"
              onClick={openCreateForm}
              disabled={isTeacherRole && !teacherGroups?.length}
              title={isTeacherRole && teacherGroups?.length === 0 ? 'No sections are assigned to you yet.' : undefined}
              className="flex items-center justify-center gap-1.5 rounded-md bg-[#0b4a71] px-3 py-2 lg:text-sm font-medium text-white hover:bg-[#3f6d8e] disabled:opacity-50 shrink-0"
            >
              <PlusIcon className="h-4 w-4" />
              Add Student
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
          {loading ? (
            <Spinner />
          ) : students.length === 0 ? (
            <p className="text-center text-gray-500 py-12">
              {isTeacherRole && teacherGroups?.length === 0
                ? 'No sections are assigned to you yet. Ask an admin to assign sections to your subjects or an advisory class.'
                : 'No students found.'}
            </p>
          ) : (
            <>
              <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#4ea6d0] text-left text-white sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 font-medium">LRN</th>
                      <th className="px-4 py-3 font-medium">Name</th>
                      <th className="px-4 py-3 font-medium">Age</th>
                      <th className="px-4 py-3 font-medium">Gender</th>
                      <th className="px-4 py-3 font-medium">Grade Level</th>
                      <th className="px-4 py-3 font-medium">Section</th>
                      <th className="px-4 py-3 font-medium">Strand</th>
                      <th className="px-4 py-3 font-medium">School Year</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {students.map((student) => (
                      <tr key={student.id} className="hover:bg-[#4ea6d0]/20">
                        <td className="px-4 py-3 text-gray-600">{student.lrn}</td>
                        <td className="px-4 py-3 font-medium text-[#0b4a71]">
                          {student.lastname}, {student.firstname} {student.middlename ? `${student.middlename} ` : ''}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{student.age ?? '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{student.gender || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{getGradeLevelLabel(student.grade_level)}</td>
                        <td className="px-4 py-3 text-gray-600">{student.section || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{student.strand || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{student.school_year || '—'}</td>
                        <td className="px-4 py-3">
                          {isSuperAdmin ? (
                            <button
                              type="button"
                              onClick={() => handleToggleApproved(student)}
                              title={student.approved ? 'Click to set to pending' : 'Click to approve'}
                              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                student.approved
                                  ? 'bg-green-50 text-green-600 hover:bg-green-100'
                                  : 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                              }`}
                            >
                              {student.approved ? 'Approved' : 'Pending'}
                            </button>
                          ) : (
                            <span
                              title={
                                student.approved
                                  ? undefined
                                  : "Until an administrator approves them they can't sign in, be graded, or appear in subject grades."
                              }
                              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                student.approved ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'
                              }`}
                            >
                              {student.approved ? 'Approved' : 'Pending'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openEditForm(student)}
                              aria-label="Edit student"
                              className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-[#0b4a71]"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(student)}
                              aria-label="Remove student"
                              className="p-1.5 rounded-md text-gray-500 hover:bg-red-50 hover:text-red-600"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={totalCount}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            </>
          )}
        </div>
      </main>

      {isFormOpen && (
        <StudentFormModal
          student={editingStudent}
          allowedGroups={!editingStudent && isTeacherRole ? teacherGroups : undefined}
          onClose={() => setIsFormOpen(false)}
          onSubmit={handleFormSubmit}
        />
      )}

      {importReview && (
        <ImportReviewModal
          {...importReview}
          allowedGroups={isTeacherRole ? teacherGroups : undefined}
          onClose={() => setImportReview(null)}
          onImport={handleImport}
        />
      )}
    </div>
  );
}

export default StudentsPage;
